// Guards the transcript against components being rendered more than once.
//
// Two things push component messages into the chat: the live response handler and
// the history rebuild on reload. Neither can see the other's output, and the
// backend replays a stage across several history entries (the talent audit's
// `done` turn repeats the timeline stage that already ran under state
// `timeline`), so the same card could be appended twice — most visibly a second
// timeline stranded below the live-assessment card.

/**
 * Components that represent a single stage of a conversation and must therefore
 * appear at most once, whatever state the backend files them under. Everything
 * else (option pills, uploaded-file chips, retry prompts, per-state controls) is
 * legitimately repeatable and is left alone.
 */
export const SINGLETON_COMPONENT_TYPES = new Set(['timeline', 'jobAssessment']);

interface DedupableMessage {
  id?: string;
  componentType?: string;
}

/**
 * Keeps the first of each singleton component and drops repeated message ids.
 * Order is preserved, so the surviving card stays where the conversation put it.
 */
export const dedupeMessages = <T extends DedupableMessage>(messages: T[]): T[] => {
  const seenComponents = new Set<string>();
  const seenIds = new Set<string>();

  return messages.filter((message) => {
    if (message.id) {
      if (seenIds.has(message.id)) return false;
      seenIds.add(message.id);
    }

    const componentType = message.componentType;
    if (!componentType || !SINGLETON_COMPONENT_TYPES.has(componentType)) return true;
    if (seenComponents.has(componentType)) return false;
    seenComponents.add(componentType);
    return true;
  });
};

/** Appends to the transcript without letting a duplicate through. */
export const appendMessages = <T extends DedupableMessage>(prev: T[], incoming: T[]): T[] =>
  dedupeMessages([...prev, ...incoming]);
